import {
  AdditionalReference,
  NormalizedLoad,
  TMSEquipmentType,
} from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';

import {
  MGB_CARRIER_REP_REF_TYPE,
  MGB_CUSTOMER_REP_REF_TYPE,
  MGB_DRUMKIT_QUOTE_NUMBER_PREFIX,
  MGB_QUOTE_NUMBER_REF_TYPE,
  MGB_RATE_MAX_REF_TYPE,
  MGB_RATE_MIN_REF_TYPE,
  MGB_TRAILER_LENGTH_REF_TYPE,
} from '../constants';
import { mapSuggestionQualifier } from './suggestionMaps';

const MGB_SPECIAL_REFERENCE_TYPES = [
  MGB_CUSTOMER_REP_REF_TYPE,
  MGB_CARRIER_REP_REF_TYPE,
  MGB_TRAILER_LENGTH_REF_TYPE,
] as const;

/** True when qualifier/id/name is the system Quote Number additional-reference type. */
export function isSystemQuoteNumberRef(qualifier: string | undefined): boolean {
  return (
    (qualifier ?? '').trim().toLowerCase() ===
    MGB_QUOTE_NUMBER_REF_TYPE.toLowerCase()
  );
}

/** True when qualifier/id/name is the system Rate Min additional-reference type. */
export function isSystemRateMinRef(qualifier: string | undefined): boolean {
  return (
    (qualifier ?? '').trim().toLowerCase() ===
    MGB_RATE_MIN_REF_TYPE.toLowerCase()
  );
}

/** True when qualifier/id/name is the system Rate Max additional-reference type. */
export function isSystemRateMaxRef(qualifier: string | undefined): boolean {
  return (
    (qualifier ?? '').trim().toLowerCase() ===
    MGB_RATE_MAX_REF_TYPE.toLowerCase()
  );
}

function specialReferenceKey(qualifier: string): string {
  const normalized = qualifier.trim().toLowerCase();
  if (!normalized) {
    return '';
  }

  return normalized.startsWith('sr:') ? normalized.slice(3).trim() : normalized;
}

/** True for dedicated MG special-reference dropdowns (Customer/Carrier Rep, Trailer Length). */
export function isSpecialReferenceRef(qualifier: string | undefined): boolean {
  const key = specialReferenceKey(qualifier ?? '');
  if (!key) {
    return false;
  }

  return MGB_SPECIAL_REFERENCE_TYPES.some(
    (type) => specialReferenceKey(type) === key
  );
}

/**
 * True for Drumkit-owned additional-reference types (Quote Number, Rate Min/Max)
 * and dedicated special-reference dropdowns. These rows are omitted from the
 * generic reference-type catalog.
 */
export function isSystemManagedRef(qualifier: string | undefined): boolean {
  return (
    isSystemQuoteNumberRef(qualifier) ||
    isSystemRateMinRef(qualifier) ||
    isSystemRateMaxRef(qualifier) ||
    isSpecialReferenceRef(qualifier)
  );
}

// Maps an AI suggestion reference qualifier using the caller's tenant table.
// System-managed types are shared across MGB tenants. Unmatched → '' so the
// row still shows for manual selection.
export function mapReferenceQualifier(
  qualifier: string | undefined,
  qualifierMap: Record<string, string>
): string {
  const trimmed = (qualifier ?? '').trim();
  if (!trimmed) {
    return '';
  }

  if (isSystemManagedRef(trimmed)) {
    if (isSystemQuoteNumberRef(trimmed)) {
      return MGB_QUOTE_NUMBER_REF_TYPE;
    }
    if (isSystemRateMinRef(trimmed)) {
      return MGB_RATE_MIN_REF_TYPE;
    }
    if (isSystemRateMaxRef(trimmed)) {
      return MGB_RATE_MAX_REF_TYPE;
    }
    // Only Customer Rep binds to the dedicated dropdown from AI suggestions.
    // Carrier Rep / Trailer Length keep prior behavior (qualifier → '').
    if (
      specialReferenceKey(trimmed) ===
      specialReferenceKey(MGB_CUSTOMER_REP_REF_TYPE)
    ) {
      return MGB_CUSTOMER_REP_REF_TYPE;
    }
  }

  return mapSuggestionQualifier(trimmed, qualifierMap);
}

// Normalizes additional references from an AI load-building suggestion.
export function normalizeMGBAdditionalReferencesFromSuggestion(
  refs: MaybeUndef<AdditionalReference[]>,
  qualifierMap: Record<string, string>
): AdditionalReference[] {
  return (refs ?? [])
    .filter((ref) => {
      const hasNumber = Boolean(ref?.number?.trim());
      const hasQualifier = Boolean(ref?.qualifier?.trim());
      return hasNumber || hasQualifier;
    })
    .map((ref) => ({
      qualifier: mapReferenceQualifier(ref?.qualifier, qualifierMap),
      number: (ref?.number ?? '').trim(),
      weight: ref?.weight,
      pieces: ref?.pieces,
      shouldSendToDriver: Boolean(ref?.shouldSendToDriver),
    }));
}

export function specialReferenceQualifierAliases(
  canonicalQualifier: string
): string[] {
  const trimmed = canonicalQualifier.trim();
  if (!trimmed) {
    return [];
  }

  const withoutPrefix = trimmed.toLowerCase().startsWith('sr:')
    ? trimmed.slice(3).trim()
    : trimmed;
  const withPrefix = trimmed.toLowerCase().startsWith('sr:')
    ? trimmed
    : `SR:${trimmed}`;

  if (withoutPrefix.toLowerCase() === withPrefix.toLowerCase()) {
    return [trimmed];
  }

  return trimmed.toLowerCase().startsWith('sr:')
    ? [withPrefix, withoutPrefix]
    : [withoutPrefix, withPrefix];
}

export function getSpecialReferenceNumber(
  refs: NormalizedLoad['additionalReferences'] | undefined,
  canonicalQualifier: string
): string {
  for (const qualifier of specialReferenceQualifierAliases(
    canonicalQualifier
  )) {
    const number = getAdditionalReferenceNumber(refs, qualifier);
    if (number) {
      return number;
    }
  }

  return '';
}

export function setSpecialReferenceNumber(
  refs: NormalizedLoad['additionalReferences'] | undefined,
  canonicalQualifier: string,
  number: string
): NonNullable<NormalizedLoad['additionalReferences']> {
  let next = refs ?? [];
  for (const qualifier of specialReferenceQualifierAliases(
    canonicalQualifier
  )) {
    if (qualifier.toLowerCase() === canonicalQualifier.trim().toLowerCase()) {
      continue;
    }
    next = setAdditionalReferenceNumber(next, qualifier, '');
  }

  return setAdditionalReferenceNumber(next, canonicalQualifier, number);
}

/** Map a stored MG value (code, display name, or leftover free text) onto the option CODE. */
export function resolveSpecialReferenceCode(
  stored: string,
  options: MaybeUndef<TMSEquipmentType[]>
): string {
  const value = stored.trim();
  if (!value) {
    return '';
  }
  if (!options?.length) {
    return value;
  }

  const match = options.find((option) => {
    const id = option.id?.trim() ?? '';
    const name = option.name?.trim() ?? '';
    return (
      id.toLowerCase() === value.toLowerCase() ||
      name.toLowerCase() === value.toLowerCase()
    );
  });

  return match?.id?.trim() || value;
}

export function canonicalizeSpecialReferenceCodes(
  refs: NormalizedLoad['additionalReferences'] | undefined,
  options: {
    customerRepOptions?: MaybeUndef<TMSEquipmentType[]>;
    carrierRepOptions?: MaybeUndef<TMSEquipmentType[]>;
    trailerLengthOptions?: MaybeUndef<TMSEquipmentType[]>;
  }
): NonNullable<NormalizedLoad['additionalReferences']> {
  const fields: Array<{
    qualifier: string;
    optionList: MaybeUndef<TMSEquipmentType[]>;
  }> = [
    {
      qualifier: MGB_CUSTOMER_REP_REF_TYPE,
      optionList: options.customerRepOptions,
    },
    {
      qualifier: MGB_CARRIER_REP_REF_TYPE,
      optionList: options.carrierRepOptions,
    },
    {
      qualifier: MGB_TRAILER_LENGTH_REF_TYPE,
      optionList: options.trailerLengthOptions,
    },
  ];

  let next = refs ?? [];
  for (const field of fields) {
    const stored = getSpecialReferenceNumber(next, field.qualifier);
    if (!stored || !field.optionList?.length) {
      continue;
    }
    next = setSpecialReferenceNumber(
      next,
      field.qualifier,
      resolveSpecialReferenceCode(stored, field.optionList)
    );
  }

  return next;
}

/** Format quote_request.id as DKQN{id}. */
export function formatDrumkitQuoteNumber(quoteRequestId: number): string {
  if (!Number.isFinite(quoteRequestId) || quoteRequestId <= 0) {
    return '';
  }

  return `${MGB_DRUMKIT_QUOTE_NUMBER_PREFIX}${Math.trunc(quoteRequestId)}`;
}

export function shouldApplySuggestionQuoteAutofill(args: {
  expectedDkqn: string;
  currentQuoteOnForm: string;
  userOverrodeQuote: boolean;
}): boolean {
  const expected = args.expectedDkqn.trim();
  if (!expected) {
    return false;
  }

  if (args.currentQuoteOnForm.trim() === expected) {
    return false;
  }

  if (args.userOverrodeQuote) {
    return false;
  }

  return true;
}

/** Parse DKQN123 or legacy bare numeric id → quote_request id (0 if invalid). */
export function parseDrumkitQuoteNumber(value: string | undefined): number {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return 0;
  }

  const upper = trimmed.toUpperCase();
  const raw = upper.startsWith(MGB_DRUMKIT_QUOTE_NUMBER_PREFIX)
    ? trimmed.slice(MGB_DRUMKIT_QUOTE_NUMBER_PREFIX.length).trim()
    : trimmed;

  const id = Number(raw);
  if (!Number.isFinite(id) || id <= 0) {
    return 0;
  }

  return Math.trunc(id);
}

/** Quote Number currently on the form → create-load quoteRequestId (omit when cleared). */
export function resolveMGBQuoteRequestIdForCreate(
  formQuoteNumber: string | undefined
): number | undefined {
  const fromForm = parseDrumkitQuoteNumber(formQuoteNumber);
  return fromForm > 0 ? fromForm : undefined;
}

/**
 * Drop valueless system-managed placeholders (Quote Number, Rate Min/Max and
 * the special-reference dropdowns). These rows are hidden from the generic
 * Type/Value list, so a user cannot fill or remove them.
 */
export function stripEmptySystemManagedRefs(
  refs: NormalizedLoad['additionalReferences'] | undefined
): NonNullable<NormalizedLoad['additionalReferences']> {
  return (refs ?? []).filter(
    (ref) =>
      !(isSystemManagedRef(ref?.qualifier) && !String(ref?.number ?? '').trim())
  );
}

function upsertAdditionalReference(
  refs: NonNullable<NormalizedLoad['additionalReferences']>,
  qualifier: string,
  number: string
): NonNullable<NormalizedLoad['additionalReferences']> {
  const next = [...refs];
  const idx = next.findIndex(
    (ref) =>
      qualifier.toLowerCase() === (ref.qualifier ?? '').trim().toLowerCase()
  );
  const row = {
    qualifier,
    number,
    weight: undefined as unknown as number,
    pieces: undefined as unknown as number,
    shouldSendToDriver: false,
  };

  if (idx >= 0) {
    next[idx] = { ...next[idx], ...row };
  } else {
    next.push(row);
  }

  return next;
}

function removeAdditionalReference(
  refs: NonNullable<NormalizedLoad['additionalReferences']>,
  qualifier: string
): NonNullable<NormalizedLoad['additionalReferences']> {
  return refs.filter(
    (ref) =>
      (ref.qualifier ?? '').trim().toLowerCase() !== qualifier.toLowerCase()
  );
}

export function setAdditionalReferenceNumber(
  refs: NormalizedLoad['additionalReferences'] | undefined,
  qualifier: string,
  number: string
): NonNullable<NormalizedLoad['additionalReferences']> {
  const base = refs ?? [];
  const trimmed = number.trim();
  if (!trimmed) {
    return removeAdditionalReference(base, qualifier);
  }

  return upsertAdditionalReference(base, qualifier, trimmed);
}

export function getAdditionalReferenceNumber(
  refs: NormalizedLoad['additionalReferences'] | undefined,
  qualifier: string
): string {
  return (
    (refs ?? [])
      .find(
        (ref) =>
          (ref.qualifier ?? '').trim().toLowerCase() === qualifier.toLowerCase()
      )
      ?.number?.trim() ?? ''
  );
}

export function formatBuyBandRateForRef(value: number): string {
  return String(Math.round(value));
}

/** Apply or clear Quote Number + optional Rate Min/Max on additionalReferences. */
export function applyQuoteNumberSelectionToRefs(
  refs: NormalizedLoad['additionalReferences'] | undefined,
  selection: {
    drumkitQuoteNumber: string;
    lowBuyRate?: number;
    highBuyRate?: number;
    /** When true, also write Rate Min/Max from the band (on pick). */
    syncRateBand?: boolean;
  }
): NonNullable<NormalizedLoad['additionalReferences']> {
  let next = stripEmptySystemManagedRefs(refs);
  const dkqn = selection.drumkitQuoteNumber.trim();
  if (!dkqn) {
    next = removeAdditionalReference(next, MGB_QUOTE_NUMBER_REF_TYPE);
    next = removeAdditionalReference(next, MGB_RATE_MIN_REF_TYPE);
    next = removeAdditionalReference(next, MGB_RATE_MAX_REF_TYPE);
    return next;
  }

  next = upsertAdditionalReference(next, MGB_QUOTE_NUMBER_REF_TYPE, dkqn);
  if (selection.syncRateBand) {
    const low = selection.lowBuyRate;
    const high = selection.highBuyRate;
    const hasLow = low != null && Number.isFinite(low) && low > 0;
    const hasHigh = high != null && Number.isFinite(high) && high > 0;

    next = hasLow
      ? upsertAdditionalReference(
          next,
          MGB_RATE_MIN_REF_TYPE,
          formatBuyBandRateForRef(low)
        )
      : removeAdditionalReference(next, MGB_RATE_MIN_REF_TYPE);
    next = hasHigh
      ? upsertAdditionalReference(
          next,
          MGB_RATE_MAX_REF_TYPE,
          formatBuyBandRateForRef(high)
        )
      : removeAdditionalReference(next, MGB_RATE_MAX_REF_TYPE);
  }

  return next;
}
